import { connect } from "react-redux";
import { ReduxState } from "../../store/state";
import NavBar from "../../components/navigation/NavBar";
import {
  Container,
  Text,
  Flex,
  Divider,
  Card,
  CardBody,
  IconButton,
  Button,
  Box
} from '@chakra-ui/react'
import { User, UserOrder, UserOrdersDetails, UserTransactionsDetails } from "../../store/user/types";
import { MdEdit, MdHistory, MdShoppingCart } from "react-icons/md";
import { useEffect, useState } from "react";
import { asyncWelcomeUser, asyncLogout } from "../../store/auth/initiators";
import { fetchPreviouslyPurchasedItems, fetchPreviouslyViewedItems } from "../../store/page/initiators";
import { BusinessItem, Transaction } from "../../store/page/types";
import MiniItemRow from "../../components/page/MiniItemRow";
import UserPhoneNumberForm from "../../components/auth/UserPhoneNumberForm";
import UserWelcomeVerifyForm from "../../components/auth/UserWelcomeVerifyForm";
import AppButton from "../../components/global/forms/AppButton";
import ViewUserViewSelector from "../../components/user/ViewUserViewSelector";
import ViewUserOrders from "../../components/user/ViewUserOrders";
import UserNameForm from "../../components/user/UserNameForm";
import { asyncUpdateUser, fetchUserOrders, fetchUserTransactions } from "../../store/user/initiators";
import ViewUserTransactions from "../../components/user/ViewUserTransactions";
import Footer from "../../components/navigation/Footer";
import { Helmet } from "react-helmet";

interface UserPageProps {
  user: User | null;
  userOrders: UserOrder[];
  userView: string;
  userOrdersDetails: UserOrdersDetails;
  loginToken: string | null;
  previouslyViewedItems: BusinessItem[];
  previouslyPurchasedItems: BusinessItem[];
  isLoggedIn: boolean;
  userTransactions: Transaction[];
  userTransactionsDetails: UserTransactionsDetails;
  fetchPreviouslyViewedItems: () => void;
  fetchPreviouslyPurchasedItems: () => void;
  asyncWelcomeUser: (altMethod?: string) => void;
  asyncLogout: (tokenId: string, userId: string) => void;
  asyncUpdateUser: (updatedUser: User) => void;
  fetchUserOrders: (userId: string, more: boolean) => void;
  fetchUserTransactions: (userId: string, more: boolean) => void;
}

function getGreeting() {
  const currentTime = new Date();
  const currentHour = currentTime.getHours();

  let greeting;

  if (currentHour < 12) {
    greeting = "Good Morning";
  } else if (currentHour < 18) {
    greeting = "Good Afternoon";
  } else {
    greeting = "Good Evening";
  }

  return greeting;
}

const UserPage: React.FC<UserPageProps> = ({ 
  userOrders, userView, user, userOrdersDetails, previouslyViewedItems, 
  previouslyPurchasedItems, isLoggedIn, loginToken, userTransactions, userTransactionsDetails,
  fetchPreviouslyViewedItems, fetchPreviouslyPurchasedItems, asyncWelcomeUser, asyncLogout,
  asyncUpdateUser, fetchUserOrders, fetchUserTransactions
}) => {

  const [authStep, setAuthStep] = useState(0);
  const [userNameFormOpen, setUserNameFormOpen] = useState(false);
  useEffect(() => {
    if (user) {
      fetchPreviouslyViewedItems();
      fetchPreviouslyPurchasedItems();
      fetchUserOrders(user._id, false);
      fetchUserTransactions(user._id, false);
    }
  }, [user])
  
  const renderUserPage = () => {
    if (isLoggedIn && user && loginToken) {
      const isNamed = user.firstName.length > 2 && user.firstName !== 'Customer';
      const nameDisplay = isNamed ? `, ${user.firstName} ${user.lastName ? user.lastName : ''}` : `. What's your name?`;
      return (
        <Container maxW={'8xl'} pt={1}>
          <Helmet>
            <title>{`Showfa Express Pages: Request. Deliver. Impress.`}</title>
            <meta name="description" content={`Showfa Express Pages: Request. Deliver. Impress.`} />
            <meta property="og:title" content={`Showfa Express Pages: Request. Deliver. Impress.`} />
            <meta property="og:description" content={`Showfa Express Pages: Request. Deliver. Impress.`} />
            <meta property="og:url" content={`/user`} />
            <meta property="og:site_name" content="Showfa Express Pages" />
          </Helmet>
        { userNameFormOpen ? (
            <Box maxW={'lg'}>
              <UserNameForm
                user={user}
                onSubmit={asyncUpdateUser}
                onClose={() => setUserNameFormOpen(false)}
              />
            </Box>
          ) :
          <>  
            <Flex flexDirection={'row'} align={'center'} pb={2} >
              <Text fontWeight={'bold'}>{`${getGreeting()}${nameDisplay}`}</Text>
              <IconButton 
                onClick={() => setUserNameFormOpen(true)}
                aria-label='Edit' 
                borderRadius={25} 
                ml={1} 
                variant={'ghost'}
                size={'sm'}
              >
                <MdEdit />
              </IconButton>
            </Flex> 
            <Text fontSize={'sm'}>Not you? <Button onClick={() =>  asyncLogout(loginToken, user._id)} size={'xs'} ml={1} mr={1}>Logout</Button></Text>  
          </>
        }
            <ViewUserViewSelector />
            <ViewUserOrders fetchUserOrders={fetchUserOrders} userView={userView} userOrders={userOrders} userOrdersDetails={userOrdersDetails} user={user}/>
            <ViewUserTransactions 
              fetchUserTransactions={fetchUserTransactions} userView={userView} 
              userTransactions={userTransactions} 
              userTransactionsDetails={userTransactionsDetails} user={user} 
            />
          { previouslyViewedItems.length ? (
            <>
            <Flex direction={'row'} alignItems={'center'} pt={2}>
              <MdHistory color="gray.600"/>
              <Text textAlign={'right'} fontWeight={'bold'} color={'gray.600'} ml={2}>Items You Viewed</Text>
            </Flex>
            <Divider></Divider>
            <MiniItemRow items={previouslyViewedItems.filter((item: BusinessItem) => (item.status === 'active' && item.pageData?.status === 'active'))} xs={true}/>
            </>
          ) : null}
          { previouslyPurchasedItems.length ? (
            <>
            <Flex direction={'row'} alignItems={'center'} pt={2}>
              <MdShoppingCart color="gray.600"/>
              <Text textAlign={'right'} fontWeight={'bold'} color={'gray.600'} ml={2}>Items You Bought</Text>
            </Flex>
            <Divider></Divider>
            <MiniItemRow items={previouslyPurchasedItems.filter((item: BusinessItem) => (item.status === 'active' && item.pageData?.status === 'active'))} xs={true}/>
            </>
          ): null}
        </Container>
      )
    }
    else if (authStep === 0) {
      return (
        <Container maxW={'lg'} pt={1}>
          <Card borderRadius={20}>
            <CardBody padding={8}>
              <Text align={'center'} fontWeight={'bold'}>Pages by Showfa Express!</Text>
              <AppButton 
                onClick={() => setAuthStep(1)} 
                text={'Continue'}
              />
            </CardBody>
          </Card>
        </Container>
      );
    }
    else if (authStep === 1) {
      if (isLoggedIn) {
        setAuthStep(3)
      }
      return (
        <Container maxW={'lg'} pt={1}>
          <UserPhoneNumberForm 
            goBack={() => setAuthStep(0)}
            goForward={() => {
              asyncWelcomeUser()
              setAuthStep(2)
            }}
            goForwardAltWA={() => {
              asyncWelcomeUser('whatsapp')
              setAuthStep(2)
            }}
          />
        </Container>
      );
    } else if (authStep === 2) {
      return (
              <Container maxW={'lg'} pt={1}>
                <UserWelcomeVerifyForm 
                  goBack={() => setAuthStep(1)}
                  goForward={() => setAuthStep(3)}
                />
              </Container>
              )
    } else if (authStep === 3) {
      if (!isLoggedIn) {
        setAuthStep(1)
      }  
    }
  }

  return (
    <Box backgroundColor={'gray.50'}>
    <NavBar/>
    {renderUserPage()}
    <Footer />
    </Box>
  );
}

const mapStateToProps = (state: ReduxState) => {
  const { isLoggedIn, loginToken } = state.auth;
  const { previouslyViewedItems, previouslyPurchasedItems } = state.page;
  const { userOrders, userView, user, userOrdersDetails, userTransactions, userTransactionsDetails } = state.user
  return { userOrders, userView, user, userOrdersDetails, previouslyViewedItems, previouslyPurchasedItems, isLoggedIn, loginToken, userTransactions, userTransactionsDetails };
};

export default connect(mapStateToProps, {
  fetchPreviouslyViewedItems,
  fetchPreviouslyPurchasedItems,
  asyncWelcomeUser,
  asyncLogout,
  asyncUpdateUser,
  fetchUserOrders,
  fetchUserTransactions
})(UserPage);

